import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { PLATFORM_TAGS, PLATFORMS } from "@/lib/platforms";

const FILTERS = [
  { key: "all", name: "Everything" },
  { key: "general", name: "Novcovek" },
  ...Object.entries(PLATFORM_TAGS).filter(([k]) => k !== "general").map(([k, v]) => ({ key: k, name: v.name })),
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch((e) => console.warn("blog load failed", e));
  }, []);

  const shown = filter === "all" ? posts : posts.filter((p) => p.platform === filter);
  const count = (k) => (k === "all" ? posts.length : posts.filter((p) => p.platform === k).length);

  return (
    <main className="pt-16" data-testid="blog-page">
      {/* ---- the masthead, warm and off-centre ---- */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -left-24 w-[620px] h-[620px] opacity-70"
          style={{ background: "radial-gradient(circle, rgba(239,234,252,0.95), transparent 66%)" }} />
        <div className="pointer-events-none absolute top-10 right-[-8%] w-[420px] h-[420px] opacity-60"
          style={{ background: "radial-gradient(circle, rgba(252,238,228,0.95), transparent 68%)" }} />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <p className="kicker text-[var(--ink-soft)]">Reading room</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">
                Written plainly,<br /><span className="text-[var(--ink-soft)]">on purpose</span>
              </h1>
              <p className="mt-5 text-[var(--ink-soft)] max-w-md">
                No hedging, no press release. Every post carries the colour of the place it belongs to, so you can read your way in.
              </p>
            </motion.div>

            <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}>
              <div className="rounded-[1.8rem] border border-[var(--line)] bg-white/55 p-6 lg:ml-8">
                <p className="kicker text-[var(--ink-soft)]">Where the writing lives</p>
                <div className="mt-4 flex items-end gap-1.5 h-16">
                  {PLATFORMS.map((p, i) => {
                    const n = posts.filter((x) => x.platform === p.key).length;
                    return (
                      <motion.button
                        key={p.key}
                        onClick={() => setFilter(p.key)}
                        className="flex-1 rounded-t-md origin-bottom"
                        style={{ background: p.hue, opacity: filter === "all" || filter === p.key ? 1 : 0.28 }}
                        initial={{ height: 4 }}
                        animate={{ height: `${28 + Math.min(n, 4) * 18}%` }}
                        transition={{ duration: 0.6, delay: 0.2 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={{ scaleY: 1.12 }}
                        title={`${p.name} · ${n}`}
                        aria-label={`${p.name}, ${n} posts`}
                        data-testid={`blog-spine-${p.key}`}
                      />
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-[var(--ink-soft)] font-mono-nc">{posts.length} pieces · seven places · one voice</p>
              </div>
            </motion.div>
          </div>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className="rounded-full border px-4 py-1.5 text-sm font-medium transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                style={{
                  borderColor: filter === f.key ? "var(--ink)" : "var(--line)",
                  background: filter === f.key ? "var(--ink)" : "rgba(255,255,255,0.5)",
                  color: filter === f.key ? "var(--paper)" : "var(--ink)",
                }}
                data-testid={`blog-filter-${f.key}`}
              >
                {f.key !== "all" && (
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: (PLATFORM_TAGS[f.key] || PLATFORM_TAGS.general).hue }} />
                )}
                {f.name}
                <span className="text-[11px] tabular-nums" style={{ opacity: 0.5 }}>{count(f.key)}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ---- the pieces ---- */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {shown.map((post, i) => {
            const tag = PLATFORM_TAGS[post.platform] || PLATFORM_TAGS.general;
            return (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={i % 2 === 1 ? "md:mt-10" : ""}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block relative rounded-[1.8rem] border border-[var(--line)] p-7 pl-9 bg-white/45 hover:bg-white/85 hover:-translate-y-1 transition-all overflow-hidden"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <span className="absolute left-0 top-0 bottom-0 w-[5px] transition-all group-hover:w-[9px]" style={{ background: tag.hue }} />
                  <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: `radial-gradient(circle, ${tag.hue}22, transparent 70%)` }} />
                  <span className="kicker inline-flex items-center gap-2" style={{ color: tag.hue }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: tag.hue }} />{tag.name}
                  </span>
                  <h2 className="font-display text-2xl mt-2.5 leading-snug">{post.title}</h2>
                  <p className="text-sm text-[var(--ink-soft)] mt-2.5 leading-relaxed">{post.excerpt}</p>
                  <p className="text-xs text-[var(--ink-soft)] mt-6 font-mono-nc">{post.author} · {post.read_minutes} min · {post.published_at}</p>
                </Link>
              </motion.div>
            );
          })}
        </div>
        {shown.length === 0 && (
          <div className="mt-6 rounded-[1.8rem] border border-dashed border-[var(--line)] p-10 text-center" data-testid="blog-empty">
            <p className="font-display text-2xl">Nothing written here yet</p>
            <p className="mt-2 text-[var(--ink-soft)] text-sm">This place is open, the writing is coming. Try another colour.</p>
            <button onClick={() => setFilter("all")} className="mt-5 btn-line rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="blog-empty-reset">
              Show everything
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
